import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoaderIcon } from '@repo/icons';
import { ContactInfo, DropdownMenuOption, ID } from '@repo/utils/types';
import { attributesForContactInfo, contactOptionsForContactInfo } from '@repo/utils/models/Contact';
import { useAuthContext, useThemedIconForSource } from '@repo/hooks';
import { selectContactSources } from '@repo/redux/reducers/data/sources';
import { fetchSources } from '@repo/redux/actions/sources';

interface ContactPersonOptionOpts {
  onOpen?: () => void;
  onOptionSelect?: () => void;
  optionClassName?: string;
}

const SourceIcon = ({ source }: { source: ContactInfo }) => {
  const icon = useThemedIconForSource(source);
  return <img src={icon} />;
};

export const useContactPersonOptions = (
  contactID: ID,
  { onOpen, onOptionSelect, optionClassName }: ContactPersonOptionOpts = {},
): [DropdownMenuOption[], () => void] => {
  const { getIsProPlan } = useAuthContext();
  const dispatch = useDispatch();
  const sources = useSelector(selectContactSources(contactID));
  const onMenuOpen = useCallback(() => {
    onOpen?.();
    if (!sources.isLoaded) {
      dispatch(fetchSources({ contact: { id: contactID } }));
    }
  }, [contactID]);

  const options = useMemo(() => {
    if (!sources.isLoaded) {
      return [{ title: 'Loading', icon: LoaderIcon, disabled: true }];
    }

    const contactInfoOpts = sources.items
      .valueSeq()
      .toArray()
      .flatMap(contactOptionsForContactInfo)
      .map((contactInfo) => {
        const { href, target, displayValue, label } = attributesForContactInfo(
          contactInfo,
          !getIsProPlan(),
        );

        if (!href) {
          return null;
        }

        const action = () => {
          onOptionSelect?.();
          if (target) {
            window.open(href, target);
          } else {
            window.location.href = href;
          }
        };

        return {
          title: (
            <span style={{ flex: 1, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayValue}</span>
              <span style={{ whiteSpace: 'nowrap', opacity: 0.6 }} className="information">
                {label}
              </span>
            </span>
          ),
          icon: () => <SourceIcon source={contactInfo} />,
          action,
          className: optionClassName,
        };
      })
      .filter((contactMethod) => !!contactMethod);

    return contactInfoOpts.length > 0
      ? contactInfoOpts
      : [{ title: 'No contact methods found for this person', disabled: true }];
  }, [contactID, sources.items]);

  return [options, onMenuOpen];
};
